import { useCurrentUser } from '@/hooks/useUserRole';

/** Suite applications a user can be granted. Mirrors the backend AppAccess enum. */
export const CONTENT_TRACKER = 'content_tracker';
export const ASSET_LIBRARY = 'asset_library';

/**
 * Whether the current user has been granted the given suite app.
 *
 * Grants come from `/users/me` (`app_access`) and are administered in the user
 * panel. This drives what the UI *offers*; it is not the control. Both suite
 * apps enforce the same grant server-side — see
 * `apps/asset-library/backend/src/core/access.py` and
 * `apps/content-tracker/backend/src/api/dependencies.py`.
 *
 * Fails closed: an absent or malformed `app_access` grants nothing, so a stale
 * or partial payload cannot open an app the user has not been given.
 *
 * `isError` is reported separately from `allowed` on purpose. A failed lookup
 * means the grant is *unknown*, not *denied* — callers should say so rather
 * than silently treating a network blip as a revoked permission.
 */
export function useAppAccess(app: string): {
  allowed: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isPending, isError, refetch } = useCurrentUser();

  const granted = data?.app_access;
  const allowed = Array.isArray(granted) && granted.includes(app);

  return { allowed, isLoading: isPending, isError, refetch };
}

export const useCanOpenAssetLibrary = () => useAppAccess(ASSET_LIBRARY);
export const useCanOpenContentTracker = () => useAppAccess(CONTENT_TRACKER);
